import { Button, useTheme } from '@mui/material';
import { Layout } from 'plotly.js';
import { useTranslation } from 'react-i18next';

import { Plot } from '@/monitoring/plots/Plot';
import { CommuteUtils } from '@/monitoring/utils/CommuteUtils';
import { HandleDownload } from '@/services/DownloadHandler';
import { CommuteType, MonitoringAnalysisData } from '@/services/api';

type DistancePlotProps = {
  data: MonitoringAnalysisData[];
  showIcons: boolean;
  includeWayBack: boolean;
  showSubtitle?: boolean;
  layout?: Partial<Layout>;
  disableDownloadButton?: boolean;
  hideBoxPoints?: boolean;
  iconSizeFactor?: number;
};

export function DistancePlot({
  data,
  showIcons,
  includeWayBack,
  showSubtitle,
  layout,
  disableDownloadButton,
  hideBoxPoints,
  iconSizeFactor = 1,
}: DistancePlotProps) {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const filteredData = data.filter((obj) => obj.count !== 0 && obj.commuteType !== CommuteType.HOME_OFFICE);
  const plotData = filteredData.map((obj) => ({
    x: obj.data.map((value) => (includeWayBack ? value.distance * 2 : value.distance)),
    type: 'box',
    name: t(`CommuteTypeShort.${obj.commuteType}`),
    marker: { color: CommuteUtils[obj.commuteType].color, size: 2 },
    line: { width: 1 },
    boxpoints: hideBoxPoints ? false : 'all',
  }));

  const header = ['Verkehrsmittel', 'Minimum', 'Q1 (25%)', 'Median', 'Q3 (75%)', 'Maximal', 'Mittelwert'];
  const csvData = plotData.map((param) => {
    const total = param.x.reduce((acc, currentValue) => acc + currentValue, 0);
    const count = param.x.length;
    const average = total !== 0 ? total / count : 0;

    const sortedValues = param.x.sort((a, b) => a - b);

    // Matches Plotly's linear quartilemethod H&L-2
    const getQuantile = (sorted: number[], q: number) => {
      if (sorted.length === 0) return 0;
      const pos = sorted.length * q - 0.5; // 0-based: (n*q + 0.5) - 1
      const clampedPos = Math.max(0, Math.min(pos, sorted.length - 1));
      const base = Math.floor(clampedPos);
      const rest = clampedPos - base;
      if (rest > 0 && sorted[base + 1] !== undefined) {
        return sorted[base] + rest * (sorted[base + 1] - sorted[base]);
      }

      return sorted[base];
    };

    const q1 = getQuantile(sortedValues, 0.25);
    const median = getQuantile(sortedValues, 0.5);
    const q3 = getQuantile(sortedValues, 0.75);

    const formatValue = (value: number) => (value === 0 ? '0 km' : `${value.toFixed(2)} km`);

    return {
      name: param.name,
      markerMin: formatValue(Math.min(...param.x)),
      q1: formatValue(q1),
      median: formatValue(median),
      q3: formatValue(q3),
      markerMax: formatValue(Math.max(...param.x)),
      average: formatValue(average),
    };
  });

  return (
    <>
      {!disableDownloadButton && (
        <Button onClick={() => HandleDownload({ csvData, header, name: 'Streckenlängen' })}>Download CSV</Button>
      )}
      <Plot
        data={plotData as any}
        layout={{
          title: {
            text: t('MonitoringAnalysisDistancePlotTitle'),
            subtitle: { text: showSubtitle ? t('MonitoringAnalysisDistancePlotSubtitle') : '&nbsp;' },
          },
          xaxis: {
            zeroline: false,
          },
          yaxis: { fixedrange: true, showticklabels: !showIcons },
          showlegend: false,
          margin: { b: 40, l: 120 },
          images: showIcons
            ? filteredData.map((obj, index) => ({
                source: `data:image/svg+xml;utf8,${encodeURIComponent(CommuteUtils[obj.commuteType].raw.replaceAll('currentColor', theme.palette.primary.main))}`,
                xref: 'paper',
                yref: 'paper',
                y: (index + 1 - 1 / plotData.length) / plotData.length,
                x: -0.02,
                sizex: 0.09 * iconSizeFactor,
                sizey: 0.09 * iconSizeFactor,
                layer: 'above',
                sizing: 'contain',
                xanchor: 'center',
                yanchor: 'top',
              }))
            : [],
          ...layout,
        }}
        config={{
          modeBarButtonsToRemove: ['lasso2d', 'zoom2d', 'pan2d', 'select2d', 'resetScale2d'],
          locale: i18n.language,
        }}
        useResizeHandler
        style={{ width: '100%', height: '100%', minHeight: '600px' }}
      />
    </>
  );
}
